/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import { PDFViewer } from '@react-pdf/renderer';
import {
    Case,
    CaseMilestone,
    CaseEngagementDoc,
    FieldEntry,
    EngagementTemplate,
    useInitialValues,
    UserAccount,
} from '../../../../data/types';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { useAccess } from '../../../../auth/useAccess';
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Divider,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    InfoLabel,
    InlineDrawer,
    makeStyles,
    Menu,
    MenuButton,
    MenuButtonProps,
    MenuGroup,
    MenuGroupHeader,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    OverlayDrawer,
    SplitButton,
    Tab,
    TabList,
    Text,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import {
    AddCircle16Regular,
    AppsListDetailFilled,
    AppsListDetailRegular,
    ArrowResetRegular,
    bundleIcon,
    ChartPersonFilled,
    ChartPersonRegular,
    ChevronDoubleRightRegular,
    ClipboardCheckmarkFilled,
    ClipboardCheckmarkRegular,
    ClipboardTaskListLtrRegular,
    Delete16Regular,
    Delete20Regular,
    Dismiss16Regular,
    Dismiss20Regular,
    DismissRegular,
    DocumentLightningRegular,
    EyeRegular,
    FlagFilled,
    FlagRegular,
    FlowchartFilled,
    FlowchartRegular,
    HandshakeFilled,
    HandshakeRegular,
    MergeRegular,
    MoneyFilled,
    MoneyRegular,
    MoreVerticalRegular,
    PersonQuestionMarkFilled,
    PersonQuestionMarkRegular,
    PuzzleCubePieceRegular,
    StackStarFilled,
    StackStarRegular,
    TableSimpleExcludeFilled,
    TableSimpleExcludeRegular,
    TargetArrowFilled,
    TargetArrowRegular,
    TextFieldRegular,
} from '@fluentui/react-icons';
import { BrandCard, Column, Paths, Row } from '../../../common';
import { useToasts } from '../../../toasts/useToasts';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { EngagementDoc } from './EngagementDoc';
import { useEngagementTemplateService } from '../../../../data/services/useEngagementTemplateService';
import { AddEngagementFieldDropdown, EngagementDocDefaultFields } from './AddEngagementFieldDropdown';
import { ConfirmationDialog } from '../../../dialogs/ConfirmationDialog';
import { TemplatesTabs } from '../../Templates/TemplatesPage';
import { EditableLabel } from '../../../controls/EditableLabel';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { EngagementFields } from './EngagementFields';
import { LinkButton } from '../../../controls/LinkButton';
import { usePartyService } from '../../../../data/services';
import { appTokens } from '../../../common/Styles/appTokens';
import { useModifierKeys } from '../../../../utilities/useModifierKeys';
import { useValidation } from '../../../../data/types/validation';
import { EngagementTabs } from './EngagementEnums';
import { useMediaSizes } from '../../../common/Styles/useMediaSizes';

const useStyles = makeStyles({
    tabList: {
        width: '100%',
    },
    tab: {
        textWrap: 'nowrap',
        width: '100%',
        height: '2.5rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ':hover': {
            backgroundColor: `${tokens.colorNeutralBackground2Hover} !important`,
        },
    },
    engDocContainer: {
        height: '90%',
    },
    titleLabel: {
        fontWeight: '700',
        fontSize: '1rem',
        paddingInline: '0px',
        minWidth: '250px',
    },
    nonInteractiveHeader: {
        '& button': {
            cursor: 'default',
        },
    },
    engagementDrawer: {
        width: '18rem',
        maxWidth: '85vw',
        height: '100%',
        borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
        boxSizing: 'border-box',
    },
    engagementDrawerBody: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '0',
    },
    engagementDrawerRail: {
        width: '2.75rem',
        minWidth: '2.75rem',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: tokens.spacingVerticalS,
        borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
        boxSizing: 'border-box',
        overflowY: 'auto',
    },
    engagementDrawerRailDivider: {
        width: '2rem',
        flexShrink: 0,
    },
    executedEngagementDocButton: {
        backgroundColor: tokens.colorBrandBackground2,
    },
});

export const EngagementPage = () => {
    const { caseForm, caseEngagementService, milestoneService } = useCaseServicesContext()!;

    const caseObj = caseForm.values as Case;
    const engagementDocs = [caseEngagementService.engagementDoc, ...(caseEngagementService.addendums || [])]
        .filter((ed): ed is CaseEngagementDoc => !!ed)
        .sort(
            (a, b) =>
                new Date(a.lifecycle?.createdOn || Date.now()).getTime() -
                new Date(b.lifecycle?.createdOn || Date.now()).getTime(),
        );

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user, account } = primaryAccount as UserAccount;
    const access = useAccess(user);
    const engagementTemplateService = useEngagementTemplateService();
    const partyService = usePartyService();
    const initValues = useInitialValues();
    const toasts = useToasts();
    const navTo = useNavigate();
    const modifierKeys = useModifierKeys();
    const { validateCaseObj } = useValidation();
    const mediaSizes = useMediaSizes();

    //State
    const [selectedTab, setSelectedTab] = useState<string>(EngagementTabs.billingTerms);
    const [showDoc, setShowDoc] = useState<boolean>(false);
    const [actionTemplate, setActionTemplate] = useState<EngagementTemplate>();
    const [confirmTemplateOpen, setConfirmTemplateOpen] = useState<boolean>();
    const [engagementDrawerOpen, setEngagementDrawerOpen] = useState<boolean>(() => !mediaSizes.isUnderLaptop);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    //Form
    const getFormValues = (ed?: CaseEngagementDoc) => {
        return {
            ...initValues.caseEngagement(caseObj.caseId as string),
            ...(ed ?? {}),
        };
    };
    const engagementForm = useFormik<CaseEngagementDoc>({
        initialValues: getFormValues(caseEngagementService.engagementDoc as CaseEngagementDoc),
        onSubmit: async (values, { resetForm }) => {
            let eDocValues: CaseEngagementDoc = values;
            try {
                //submit new case type
                eDocValues = await caseEngagementService.upsertCaseEngagementDocAsync(values as CaseEngagementDoc);
            } catch {
                toasts.engagement.notifyCaseEngagementUpdateError(caseObj);
            }

            resetForm({ values: eDocValues });
        },
    });

    const resetForm = (eDoc: CaseEngagementDoc) => {
        engagementForm.resetForm({
            values: getFormValues(eDoc),
        });
        selectEngagementDoc(eDoc);
    };

    //Logic
    const isSaved = () => {
        return !!engagementForm.values.lifecycle?.createdOn ? true : false;
    };
    const canEditEngagement = (): boolean => {
        return access.cases.canEditCaseEngagement(caseObj?.caseId) || false;
    };
    const clickInput = (tab: string) => {
        setSelectedTab(tab);

        if (mediaSizes.isUnderLaptop) {
            setEngagementDrawerOpen(false);
        }

        const allFocusable = document.querySelectorAll<HTMLElement>('[data-name]');
        const target = Array.from(allFocusable).find((el) => el.dataset.name === `fields.${tab}` || el.dataset.name === tab);
        target?.focus();
    };

    //Fields
    const addField = (fieldName?: string, defaultText?: string) => {
        if (!!fieldName && !engagementForm.values.fields?.some((x) => x.fieldName == fieldName)) {
            const fieldsArray = engagementForm.values.fields || [];
            engagementForm.setFieldValue('fields', [...fieldsArray, { fieldName, fieldValue: defaultText || '' }]);
        }
    };
    const removeField = (e: React.MouseEvent, fieldName?: string) => {
        if (!!fieldName) {
            const entries = engagementForm.values.fields?.filter((x) => x.fieldName != fieldName);
            engagementForm.setFieldValue('fields', entries);
        }
        e.stopPropagation();
    };
    const moveFieldUp = (fieldName: string) => {
        const fieldsArray = engagementForm.values.fields || [];
        const index = fieldsArray.findIndex((x) => x.fieldName == fieldName);

        const newArray = [
            ...fieldsArray.slice(0, index - 1),
            fieldsArray[index],
            fieldsArray[index - 1],
            ...fieldsArray.slice(index + 1),
        ];

        engagementForm.setFieldValue('fields', newArray);
    };
    const moveFieldDown = (fieldName: string) => {
        const fieldsArray = engagementForm.values.fields || [];
        const index = fieldsArray.findIndex((x) => x.fieldName == fieldName);

        const newArray = [
            ...fieldsArray.slice(0, index),
            fieldsArray[index + 1],
            fieldsArray[index],
            ...fieldsArray.slice(index + 2),
        ];

        engagementForm.setFieldValue('fields', newArray);
    };

    //Templates
    const clickApplyTemplate = (engTemplate: EngagementTemplate) => {
        setActionTemplate(engTemplate);
        if (shouldMergeTemplate()) {
            applyTemplate(engTemplate);
        } else {
            setConfirmTemplateOpen(true);
        }
    };
    const applyTemplate = (engTemplate: EngagementTemplate) => {
        if (shouldMergeTemplate()) {
            engagementTemplateService.mergeInEngagementDocTemplate(engagementForm, engTemplate);
        } else {
            engagementTemplateService.applyEngagementDocTemplate(engagementForm, engTemplate);
        }
    };
    const routeToTemplates = () => {
        navTo(Paths.templates, {
            state: { tab: TemplatesTabs.engagementTemplates },
        });
    };

    //UI Events
    const createTemplateClickHandler = (template: EngagementTemplate) => (ev: React.MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        clickApplyTemplate(template);
    };
    const handleSaveClick = (ev: React.MouseEvent) => {
        ev.stopPropagation();
        engagementForm.submitForm();
    };
    const handleUndoChanges = (ev: React.MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        engagementForm.resetForm({ values: getFormValues() });
    };
    const handleToggleShowDoc = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.stopPropagation();
        setShowDoc((prev) => !prev);
    };
    const handleConfirmApplyTemplate = (ev?: React.MouseEvent) => {
        ev?.stopPropagation();
        applyTemplate(actionTemplate!);
    };
    const handleConfirmTemplateOpenChange = (isOpen: boolean) => {
        setConfirmTemplateOpen(isOpen);
        setActionTemplate(undefined);
    };
    const handleExecuteDocument = async (ev: any) => {
        ev.stopPropagation();

        const updatedEDoc = await caseEngagementService.executeCaseEngagementDocAsync(
            engagementForm?.values.engagementDocId as string,
        );
        resetForm(updatedEDoc);
    };
    const handleUnExecuteDocument = async (ev: any) => {
        ev.stopPropagation();

        const updatedEDoc = await caseEngagementService.unExecuteCaseEngagementDocAsync(
            engagementForm?.values.engagementDocId as string,
        );
        resetForm(updatedEDoc);
    };
    const selectEngagementDoc = (ed: CaseEngagementDoc) => {
        if (engagementForm.values.engagementDocId === ed.engagementDocId && !engagementForm.values.isExecuted) {
            return;
        }
        engagementForm.resetForm({ values: getFormValues(ed) });
    };
    const clickChangeRequest = () => {
        const newDoc = caseEngagementService.createNewAddendumDoc();
        selectEngagementDoc(newDoc);
    };
    const clickDeleteAddendum = (engDocId: string) => () => {
        caseEngagementService.deleteAddendum(engDocId);
    };

    //Logic
    const getParty = () => {
        return partyService.findParty(caseObj?.retainedBy?.party?.partyId as string);
    };
    const sortMilestones = (a: CaseMilestone, b: CaseMilestone) => {
        return (a.sortKey || 0) - (b.sortKey || 0);
    };
    const canShowDoc = () => {
        return caseObj && isSaved() && milestoneService.milestones && account;
    };
    const canEditDocLabel = () => {
        return canEditEngagement() && !showDoc && !engagementForm.values.isExecuted;
    };
    const canShowApplyTemplateMenu = () => {
        return !showDoc && !engagementForm.values.isExecuted;
    };
    const canShowDocFieldsToggle = () => {
        return !!isSaved() && !engagementForm.values.isExecuted;
    };
    const canShowExecuteButton = () => {
        return !!isSaved() && !engagementForm.dirty && !engagementForm.values.isExecuted;
    };
    const canShowSaveButton = () => {
        return !!engagementForm.dirty && !engagementForm.values.isExecuted;
    };
    const canShowDocFields = () => {
        return !showDoc && !engagementForm.values.isExecuted;
    };
    const canShowDocPdf = () => {
        return !!caseObj && !!isSaved() && !!account && (showDoc || engagementForm.values.isExecuted);
    };
    const shouldMergeTemplate = () => {
        return account?.settings?.mergeInEngagementDocTemplateOnApply || modifierKeys.alt;
    };

    //Memoize the PDF document to prevent unnecessary reloads
    const memoizedPdfDocument = React.useMemo(() => {
        if (!canShowDocPdf()) {
            return null;
        }

        return (
            <EngagementDoc
                caseObj={caseObj}
                client={getParty()}
                engagementDoc={engagementForm.values as CaseEngagementDoc}
                milestones={milestoneService.milestones.sort(sortMilestones)}
                account={account}
                validateCaseObj={validateCaseObj}
            />
        );
    }, [showDoc, caseObj, engagementForm.values, account]);

    //Effects
    useEffect(() => {
        if (!!engagementForm.values.addSignatures) {
            if (!engagementForm.values.accountSignatures?.length) {
                engagementForm.setFieldValue('accountSignatures', caseObj.assignedUsers || []);
            }
            if (!engagementForm.values.clientSignatures?.length) {
                engagementForm.setFieldValue('clientSignatures', caseObj.retainedBy?.contacts || []);
            }
        }
    }, [engagementForm.values.addSignatures]);

    useEffect(() => {
        if (caseEngagementService.engagementDoc) {
            resetForm(caseEngagementService.engagementDoc);
        }
    }, [caseEngagementService.engagementDoc, caseObj.caseId]);

    useEffect(() => {
        setEngagementDrawerOpen(!mediaSizes.isUnderLaptop);
    }, [mediaSizes.isUnderLaptop]);

    const isSelectedDoc = (ed: CaseEngagementDoc) => {
        return engagementForm.values.engagementDocId === ed.engagementDocId;
    };

    const engagementDrawerContent = (
        <>
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Tooltip content='Close engagement documents' relationship='label'>
                            <Button
                                appearance='subtle'
                                aria-label='Close engagement documents'
                                icon={<Dismiss16Regular />}
                                onClick={() => setEngagementDrawerOpen(false)}
                                data-testid='engagement-page-close-drawer-btn'
                            />
                        </Tooltip>
                    }
                >
                    <Text weight='semibold'>Engagement Documents</Text>
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody className={styles.engagementDrawerBody}>
                <Column gap='small' flex='1' maxHeight={appTokens.pageHeight('15rem')} padding='pr-1' scroll>
                    <Accordion
                        collapsible
                        openItems={[engagementForm.values.engagementDocId]}
                        data-testid='engagement-page-documents-accordion'
                    >
                        {engagementDocs.map((ed) => (
                            <AccordionItem value={ed.engagementDocId || 'new'} key={ed.engagementDocId || 'new'}>
                                <Column padding='px-2 py-1'>
                                    <Column
                                        subtleBorder
                                        noGap
                                        borderRadius='8px'
                                        padding='px-1 py-1'
                                        backgroundColor={isSelectedDoc(ed) ? tokens.colorNeutralBackground1 : undefined}
                                        onClick={() => selectEngagementDoc(ed)}
                                    >
                                        <AccordionHeader
                                            size='medium'
                                            expandIcon={<></>}
                                            className={engagementDocs.length > 1 ? undefined : styles.nonInteractiveHeader}
                                        >
                                            <Column noGap overflowX='clip'>
                                                {ed.isAddendum && <Text size={200}>Addendum:</Text>}
                                                <Tooltip
                                                    content={
                                                        <Text size={200}>
                                                            {ed.isAddendum && <Text size={200}>Addendum: </Text>}
                                                            {ed?.title || 'New Engagement Document'}
                                                            {ed.isExecuted && (
                                                                <Text size={200} italic>
                                                                    {' '}
                                                                    - Executed
                                                                </Text>
                                                            )}
                                                        </Text>
                                                    }
                                                    relationship='label'
                                                >
                                                    <Text
                                                        size={300}
                                                        weight='semibold'
                                                        style={{
                                                            color: isSelectedDoc(ed)
                                                                ? tokens.colorBrandForeground2
                                                                : undefined,
                                                            textWrap: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {isSelectedDoc(ed)
                                                            ? engagementForm?.values.title
                                                            : ed?.title || 'New'}
                                                    </Text>
                                                </Tooltip>
                                                <Column align='right'>
                                                    {ed.isExecuted && (
                                                        <Text className={commonStyles.text.subtleText} size={200} italic>
                                                            Executed
                                                        </Text>
                                                    )}
                                                </Column>
                                            </Column>
                                        </AccordionHeader>
                                        <AccordionPanel>
                                            <Column noGap padding='pl-3'>
                                                {isSelectedDoc(ed) && !ed?.isExecuted && (
                                                    <EngagementDocTabs
                                                        addField={addField}
                                                        canAddField={!!canEditEngagement() && !showDoc}
                                                        engagementDoc={engagementForm.values as CaseEngagementDoc}
                                                        selectedTab={selectedTab}
                                                        showDoc={showDoc}
                                                        clickInput={clickInput}
                                                        routeToTemplates={routeToTemplates}
                                                        createTemplateClickHandler={createTemplateClickHandler}
                                                        onDeleteAddendum={clickDeleteAddendum(ed.engagementDocId as string)}
                                                    />
                                                )}
                                            </Column>
                                        </AccordionPanel>
                                    </Column>
                                </Column>
                            </AccordionItem>
                        ))}
                    </Accordion>
                    <Column flex='0' align='left'>
                        {!!engagementDocs.every((x) => x.isExecuted) && (
                            <LinkButton text='Change Request' icon={<AddCircle16Regular />} onClick={clickChangeRequest} />
                        )}
                    </Column>
                </Column>
            </DrawerBody>
        </>
    );

    return (
        <Column flex='1' padding='px-1 pb-1'>
            <Row flex='1' gap='small' noWrap>
                {!engagementDrawerOpen && (
                    <Column className={styles.engagementDrawerRail} flex='0' gap='small'>
                        <Column height='auto'>
                            <Tooltip content='Show engagement documents' relationship='label' positioning='after'>
                                <Button
                                    appearance='subtle'
                                    aria-label='Show engagement documents'
                                    icon={<ChevronDoubleRightRegular />}
                                    onClick={() => setEngagementDrawerOpen(true)}
                                    data-testid='engagement-page-open-drawer-btn'
                                />
                            </Tooltip>
                        </Column>
                        <Column height='auto' padding='py-2'>
                            <Divider className={styles.engagementDrawerRailDivider} />
                        </Column>
                        <Column flex='0' gap='small' align='center' width='100%'>
                            {engagementDocs.map((engagementDoc) => {
                                const tooltipText = (
                                    <Text size={200}>
                                        {engagementDoc.isAddendum && <Text size={200}>Addendum: </Text>}
                                        {engagementDoc?.title || 'New Engagement Document'}
                                        {engagementDoc.isExecuted && (
                                            <Text size={200} italic>
                                                {' '}
                                                - Executed
                                            </Text>
                                        )}
                                    </Text>
                                );
                                const isSelected = isSelectedDoc(engagementDoc);

                                return (
                                    <Tooltip
                                        key={engagementDoc.engagementDocId || 'new'}
                                        content={tooltipText}
                                        relationship='label'
                                        positioning='after'
                                    >
                                        <Button
                                            className={
                                                engagementDoc.isExecuted && !isSelected
                                                    ? styles.executedEngagementDocButton
                                                    : undefined
                                            }
                                            appearance={isSelected ? 'primary' : 'subtle'}
                                            aria-label={tooltipText}
                                            aria-pressed={isSelected}
                                            icon={<ClipboardTaskListLtrRegular />}
                                            onClick={() => selectEngagementDoc(engagementDoc)}
                                            data-testid={`engagement-page-rail-doc-${engagementDoc.engagementDocId || 'new'}`}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Column>
                    </Column>
                )}
                {mediaSizes.isUnderLaptop ? (
                    <OverlayDrawer
                        className={styles.engagementDrawer}
                        open={engagementDrawerOpen}
                        position='start'
                        onOpenChange={(_, data) => setEngagementDrawerOpen(data.open)}
                        data-testid='engagement-page-documents-drawer'
                    >
                        {engagementDrawerContent}
                    </OverlayDrawer>
                ) : (
                    <InlineDrawer
                        className={styles.engagementDrawer}
                        open={engagementDrawerOpen}
                        position='start'
                        data-testid='engagement-page-documents-drawer'
                    >
                        {engagementDrawerContent}
                    </InlineDrawer>
                )}

                {/* Card with fields and view doc */}
                <Column flex='5' padding='px-2 py-1' maxHeight={appTokens.pageHeight('10.5rem')}>
                    <BrandCard
                        flex='1'
                        title={
                            <>
                                <Row noGap>
                                    {engagementForm.values.isAddendum && (
                                        <Column width='auto' vAlign='middle'>
                                            <Text size={400} weight='semibold'>
                                                Addendum -{' '}
                                            </Text>
                                        </Column>
                                    )}
                                    <EditableLabel
                                        labelClassName={styles.titleLabel}
                                        required
                                        name='title'
                                        tabIndex={0}
                                        readOnly={!canEditDocLabel()}
                                        value={engagementForm.values.title}
                                        onChange={engagementForm?.handleChange}
                                        placeholder='Enter title, e.g Scope Of Work'
                                    />
                                </Row>
                            </>
                        }
                        headerIcon={<ClipboardTaskListLtrRegular />}
                        action={
                            <Column vAlign='middle' padding='pr-2'>
                                <Row gap='medium'>
                                    {!!canEditEngagement() && (
                                        <>
                                            <ApplyEngagementTemplateMenu
                                                show={canShowApplyTemplateMenu()}
                                                templates={engagementTemplateService.engagementTemplates || []}
                                                routeToTemplates={routeToTemplates}
                                                createTemplateClickHandler={createTemplateClickHandler}
                                                shouldMerge={shouldMergeTemplate()}
                                            />

                                            <ShowDocOrFieldsButton
                                                show={canShowDocFieldsToggle()}
                                                showDoc={showDoc}
                                                handleToggleShowDoc={handleToggleShowDoc}
                                                disabled={engagementForm.dirty || !canShowDoc()}
                                            />

                                            <Divider vertical />

                                            <ExecutionButton
                                                show={canShowExecuteButton()}
                                                engTitle={engagementForm.values.title}
                                                handleExecuteDocument={handleExecuteDocument}
                                            />

                                            <SaveButton
                                                show={canShowSaveButton()}
                                                handleSaveClick={handleSaveClick}
                                                handleUndoChanges={handleUndoChanges}
                                            />
                                        </>
                                    )}
                                    {engagementForm.values.isExecuted && (
                                        <>
                                            <Column vAlign='middle'>
                                                <Text size={300} italic className={commonStyles.text.subtleText}>
                                                    Executed
                                                </Text>
                                            </Column>
                                            {access.account.isAccountAdmin() && (
                                                <Column>
                                                    <>
                                                        <Menu>
                                                            <MenuTrigger disableButtonEnhancement>
                                                                <Tooltip content='More' relationship='label'>
                                                                    <Button
                                                                        icon={<MoreVerticalRegular />}
                                                                        data-testid='engagement-executed-more-options-button'
                                                                    />
                                                                </Tooltip>
                                                            </MenuTrigger>
                                                            <MenuPopover>
                                                                <MenuList>
                                                                    <MenuItem
                                                                        icon={<ArrowResetRegular />}
                                                                        onClick={handleUnExecuteDocument}
                                                                        data-testid='engagement-executed-unexecute-menu-item'
                                                                    >
                                                                        Unexecute Document
                                                                    </MenuItem>
                                                                </MenuList>
                                                            </MenuPopover>
                                                        </Menu>
                                                    </>
                                                </Column>
                                            )}
                                        </>
                                    )}
                                </Row>
                            </Column>
                        }
                    >
                        {canShowDocFields() && (
                            <Column padding='px-3 pt-2' maxHeight={appTokens.pageHeight('16rem')} scroll>
                                <EngagementFields
                                    canEdit={canEditEngagement}
                                    caseObj={caseObj}
                                    engForm={engagementForm}
                                    moveFieldUp={moveFieldUp}
                                    moveFieldDown={moveFieldDown}
                                    removeField={removeField}
                                    setSelectedTab={(name: string) => setSelectedTab(name)}
                                />
                            </Column>
                        )}

                        {canShowDocPdf() && (
                            <Column className={styles.engDocContainer} noGap>
                                {!!memoizedPdfDocument && (
                                    <PDFViewer style={{ height: '100%' }}>{memoizedPdfDocument}</PDFViewer>
                                )}
                            </Column>
                        )}
                    </BrandCard>
                </Column>
            </Row>
            <ConfirmationDialog
                title='Confirm Apply Template'
                icon={<PuzzleCubePieceRegular />}
                text={
                    <Column>
                        <Text size={400}>Are you sure you want to apply this Engagement Template?</Text>
                        <Text size={300}>
                            <Text size={300} weight='semibold'>
                                Note:{' '}
                            </Text>
                            Doing so will remove existing fields and add fields from the template with their respective
                            default text.
                        </Text>
                    </Column>
                }
                open={confirmTemplateOpen}
                onOpenChange={handleConfirmTemplateOpenChange}
                onConfirm={handleConfirmApplyTemplate}
            />
        </Column>
    );
};

type ApplyEngagementTemplateMenuProps = {
    show: boolean;
    templates: EngagementTemplate[];
    text?: string;
    routeToTemplates: () => void;
    createTemplateClickHandler: (template: EngagementTemplate) => (ev: React.MouseEvent<HTMLElement>) => void;
    appearance?: 'primary' | 'secondary' | 'subtle';
    info?: string;
    size?: 'small' | 'medium' | 'large';
    shouldMerge?: boolean;
};
const ApplyEngagementTemplateMenu = (props: ApplyEngagementTemplateMenuProps) => {
    const {
        show,
        templates,
        text,
        routeToTemplates,
        createTemplateClickHandler,
        appearance = 'secondary',
        info,
        size = 'medium',
        shouldMerge = false,
    } = props;

    if (!show) {
        return null;
    }

    return (
        <Menu positioning='below-end' data-testid='engagement-page-apply-template-menu'>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip
                    content={
                        shouldMerge ? (
                            <Text size={200}>Merge Template</Text>
                        ) : (
                            <Column gap='small'>
                                <Text size={200}>Apply Template</Text>
                                <Text size={200}>[Alt] Merge</Text>
                            </Column>
                        )
                    }
                    relationship='description'
                >
                    <InfoLabel info={info} size={size}>
                        <MenuButton
                            icon={shouldMerge ? <MergeRegular /> : <PuzzleCubePieceRegular />}
                            appearance={appearance}
                            data-testid='engagement-page-apply-template-btn'
                            size={size}
                        >
                            {text}
                        </MenuButton>
                    </InfoLabel>
                </Tooltip>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {!templates?.length ? (
                        <MenuItem onClick={routeToTemplates} data-testid='engagement-page-create-template-menu-header'>
                            <Column noGap align='center' width='auto'>
                                <Text>No templates found.</Text>
                                <Text>Click here to create a Template</Text>
                            </Column>
                        </MenuItem>
                    ) : (
                        <MenuGroup>
                            <MenuGroupHeader>
                                {shouldMerge ? (
                                    <Text size={200}>Merge Template</Text>
                                ) : (
                                    <Text size={200}>Apply Template - [Alt] Merge</Text>
                                )}
                            </MenuGroupHeader>
                            <Divider />
                            {templates.map((template) => (
                                <MenuItem
                                    key={template.templateId}
                                    onClick={createTemplateClickHandler(template)}
                                    data-testid={`engagement-page-template-item-${template.templateId}`}
                                >
                                    {template.templateName}
                                </MenuItem>
                            ))}
                        </MenuGroup>
                    )}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

type ExecutionButtonProps = {
    engTitle?: string;
    handleExecuteDocument: (ev: any) => void;
    disabled?: boolean;
    show: boolean;
};
const ExecutionButton = (props: ExecutionButtonProps) => {
    const { engTitle, handleExecuteDocument, disabled, show } = props;

    //Styles
    const commonStyles = useCommonStyles();

    if (!show) {
        return null;
    }

    return (
        <ConfirmationDialog
            title={`Confirm Execute ${engTitle}`}
            icon={<DocumentLightningRegular />}
            text={
                <Column padding='pt-2'>
                    <Text size={400}>Are you sure you want to execute the {engTitle}?</Text>
                    <Column gap='small' padding='pt-2'>
                        <Text size={300} className={commonStyles.text.subtleText}>
                            <Text size={300} weight='bold'>
                                NOTE:
                            </Text>{' '}
                            Executing this document will finalize the document in its current version. Any changes will need
                            to be made by creating a Change Order.
                        </Text>
                        <Text size={300} className={commonStyles.text.subtleText}>
                            Only an account admin can reverse this action.
                        </Text>
                    </Column>
                </Column>
            }
            onConfirm={handleExecuteDocument}
        >
            <Tooltip content='Execute Document' relationship='description'>
                <Button
                    className={commonStyles.button.root}
                    appearance='secondary'
                    disabled={disabled}
                    data-testid='engagement-page-execute-doc-btn'
                    icon={<DocumentLightningRegular />}
                >
                    Execute
                </Button>
            </Tooltip>
        </ConfirmationDialog>
    );
};

type ShowDocOrFieldsButtonProps = {
    show: boolean;
    showDoc: boolean;
    disabled?: boolean;
    handleToggleShowDoc?: (ev: any) => void;
};
const ShowDocOrFieldsButton = (props: ShowDocOrFieldsButtonProps) => {
    const { show, showDoc, disabled, handleToggleShowDoc } = props;

    //Styles
    const commonStyles = useCommonStyles();

    if (!show) {
        return null;
    }

    return (
        <Tooltip content={showDoc ? 'Show Fields' : 'View Document'} relationship='description'>
            <Button
                className={commonStyles.button.root}
                appearance='secondary'
                disabled={disabled}
                onClick={handleToggleShowDoc}
                data-testid='engagement-page-toggle-doc-btn'
                icon={showDoc ? <TextFieldRegular /> : <EyeRegular />}
            />
        </Tooltip>
    );
};

type SaveButtonProps = {
    show: boolean;
    handleSaveClick: (ev: any) => void;
    handleUndoChanges: (ev: any) => void;
};
const SaveButton = (props: SaveButtonProps) => {
    const { show, handleSaveClick, handleUndoChanges } = props;

    if (!show) {
        return null;
    }

    return (
        <Menu positioning='below-end'>
            <MenuTrigger disableButtonEnhancement>
                {(triggerProps: MenuButtonProps) => (
                    <SplitButton
                        menuButton={{
                            ...triggerProps,
                            appearance: 'primary',
                            style: {
                                borderLeft: '1px solid white',
                            },
                        }}
                        primaryActionButton={{
                            onClick: handleSaveClick,
                            appearance: 'primary',
                        }}
                        data-testid='engagement-page-save-btn'
                    >
                        Save
                    </SplitButton>
                )}
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    <MenuItem
                        icon={<ArrowResetRegular />}
                        onClick={handleUndoChanges}
                        data-testid='engagement-page-undo-changes-item'
                    >
                        Undo Changes
                    </MenuItem>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

type EngagementDocTabsProps = {
    engagementDoc: CaseEngagementDoc;
    showDoc: boolean;
    clickInput: (tab: string) => void;
    canAddField: boolean;
    addField: (fieldName?: string) => void;
    selectedTab: string;
    routeToTemplates: () => void;
    createTemplateClickHandler: (template: EngagementTemplate) => (ev: React.MouseEvent<HTMLElement>) => void;
    onDeleteAddendum?: () => void;
};
const EngagementDocTabs = (props: EngagementDocTabsProps) => {
    const {
        engagementDoc,
        showDoc,
        clickInput,
        canAddField,
        addField,
        selectedTab,
        routeToTemplates,
        createTemplateClickHandler,
        onDeleteAddendum,
    } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const engagementTemplateService = useEngagementTemplateService();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const tabClasses = mergeClasses(styles.tab, commonStyles.button.root);

    //Logic
    const createTabClickHandler = (fieldName: string) => (ev: React.MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        clickInput && clickInput(fieldName);
    };
    const mustAddWithTemplate = () => {
        return !!(
            primaryAccount?.account.settings?.mustUseTemplateToCreateEngagement &&
            !!engagementTemplateService.engagementTemplates?.length
        );
    };
    const onClickDeleteAddendum = (ev?: React.MouseEvent) => {
        ev?.stopPropagation();
        onDeleteAddendum && onDeleteAddendum();
    };

    //Icons
    const ObjectivesIcons = bundleIcon(TargetArrowFilled, TargetArrowRegular);
    const ClientRespIcons = bundleIcon(ChartPersonFilled, ChartPersonRegular);
    const ServicesIcons = bundleIcon(StackStarFilled, StackStarRegular);
    const DeliverablesIcons = bundleIcon(ClipboardCheckmarkFilled, ClipboardCheckmarkRegular);
    const MilestonesIcons = bundleIcon(FlagFilled, FlagRegular);
    const AssumptionsIcons = bundleIcon(PersonQuestionMarkFilled, PersonQuestionMarkRegular);
    const ExclusionsIcons = bundleIcon(TableSimpleExcludeFilled, TableSimpleExcludeRegular);
    const DependenciesIcons = bundleIcon(FlowchartFilled, FlowchartRegular);
    const AcceptanceIcons = bundleIcon(HandshakeFilled, HandshakeRegular);
    const PaymentIcons = bundleIcon(MoneyFilled, MoneyRegular);
    const CustomIcons = bundleIcon(AppsListDetailFilled, AppsListDetailRegular);

    const getIconForField = (fieldName: string) => {
        if (fieldName == EngagementDocDefaultFields.objectives) return <ObjectivesIcons />;
        else if (fieldName == EngagementDocDefaultFields.clientResponsibilities) return <ClientRespIcons />;
        else if (fieldName == EngagementDocDefaultFields.servicesProvided) return <ServicesIcons />;
        else if (fieldName.includes('Deliverables')) return <DeliverablesIcons />;
        else if (fieldName == EngagementDocDefaultFields.milestones) return <MilestonesIcons />;
        else if (fieldName == EngagementDocDefaultFields.assumptions) return <AssumptionsIcons />;
        else if (fieldName == EngagementDocDefaultFields.exclusions) return <ExclusionsIcons />;
        else if (fieldName == EngagementDocDefaultFields.dependencies) return <DependenciesIcons />;
        else if (fieldName == EngagementDocDefaultFields.acceptanceCriteria) return <AcceptanceIcons />;
        else if (fieldName == EngagementDocDefaultFields.billingTerms) return <PaymentIcons />;
        else if (fieldName == EngagementDocDefaultFields.paymentTerms) return <PaymentIcons />;
        else return <CustomIcons />;
    };

    return (
        <>
            <TabList
                className={styles.tabList}
                selectedValue={selectedTab}
                vertical
                disabled={showDoc}
                data-testid='engagement-page-field-tab-list'
            >
                {engagementDoc.fields?.map((field: FieldEntry) => (
                    <Tooltip content={field.fieldName || ''} relationship='label' key={field.fieldName}>
                        <Tab
                            key={field.fieldName}
                            className={tabClasses}
                            value={field.fieldName}
                            icon={getIconForField(field.fieldName as string)}
                            onClick={createTabClickHandler(field.fieldName as string)}
                            data-testid={`engagement-page-field-tab-${(field.fieldName || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                            {field.fieldName}
                        </Tab>
                    </Tooltip>
                ))}
            </TabList>

            {!!engagementDoc.fields?.length && <Divider style={{ maxHeight: '.25rem', paddingBlock: '.5rem' }} />}

            <Column gap='small' flex='1' align='left' padding='pb-2'>
                {!!canAddField && !mustAddWithTemplate() && (
                    <AddEngagementFieldDropdown
                        addField={addField}
                        addFieldDisabled={mustAddWithTemplate()}
                        info={
                            mustAddWithTemplate()
                                ? 'Your account requires using a template to add fields. Please apply a template to add fields.'
                                : undefined
                        }
                        fieldNames={engagementDoc.fields?.map((field: any) => field.fieldName as string) || []}
                    />
                )}
                {!!mustAddWithTemplate() && (
                    <ApplyEngagementTemplateMenu
                        text='Apply Template'
                        size='small'
                        info='Your account requires using a template to add fields. Please apply a template to add/remove fields.'
                        appearance='subtle'
                        show={mustAddWithTemplate()}
                        templates={engagementTemplateService.engagementTemplates || []}
                        routeToTemplates={() => routeToTemplates()}
                        createTemplateClickHandler={(template) => createTemplateClickHandler(template)}
                    />
                )}
                {!engagementDoc.isExecuted && engagementDoc.isAddendum && (
                    <Column flex='1' width='100%' align='right'>
                        <ConfirmationDialog
                            title='Confirm Delete Addendum'
                            text={`Are you sure you want to delete Addendum - ${engagementDoc.title}?`}
                            icon={<Delete20Regular />}
                            onConfirm={onClickDeleteAddendum}
                        >
                            <Button
                                size='small'
                                style={{ backgroundColor: tokens.colorStatusDangerBackground3, color: 'white' }}
                                data-testid='engagement-page-delete-addendum-btn'
                                icon={<Delete16Regular />}
                            >
                                Delete
                            </Button>
                        </ConfirmationDialog>
                    </Column>
                )}
            </Column>
        </>
    );
};
